import React from 'react';
import { Check, Globe, ArrowRight, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { webDesignPackages, webDesignAddOns } from '../data/mockData';

const PackageCard = ({ pkg }) => {
  return (
    <div 
      className={`relative p-8 flex flex-col h-full ${
        pkg.highlighted 
          ? 'bg-[#c9a962]' 
          : 'bg-[#141414] border border-[#2a2a2a] text-white'
      }`}
    >
      {/* Popular Badge */}
      {pkg.highlighted && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-[#0a0a0a] text-[#c9a962] text-xs font-medium uppercase tracking-wider px-4 py-2">
            Most Popular
          </span>
        </div>
      )}
      
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Globe size={20} className={pkg.highlighted ? 'text-white' : 'text-[#c9a962]'} />
          <h3 className={`heading-2 ${pkg.highlighted ? 'text-white' : ''}`}>{pkg.name}</h3>
        </div>
        <p className={`body-small ${pkg.highlighted ? 'text-white/80' : 'text-[#c9a962]'}`}>
          {pkg.subtitle}
        </p>
      </div>
      
      {/* Price */}
      <div className="mb-6">
        {pkg.priceNote && (
          <span className={`text-sm ${pkg.highlighted ? 'text-white/80' : 'text-[#808080]'}`}>
            {pkg.priceNote}{' '}
          </span>
        )}
        <span className={`text-4xl md:text-5xl font-medium ${pkg.highlighted ? 'text-white' : ''}`}>{pkg.price}</span>
      </div>
      
      {/* Description */}
      <p className={`body-regular mb-6 ${pkg.highlighted ? 'text-white/90' : 'text-[#b3b3b3]'}`}>
        {pkg.description}
      </p>
      
      {/* Ideal For */}
      <p className={`body-small mb-6 ${pkg.highlighted ? 'text-white/70' : 'text-[#808080]'}`}>
        <span className="font-medium">Ideal for:</span> {pkg.idealFor}
      </p>
      
      {/* Features */}
      <ul className="space-y-3 mb-8 flex-grow">
        {pkg.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5 ${
              pkg.highlighted ? 'bg-white' : 'bg-[#c9a962]'
            }`}>
              <Check size={12} className={pkg.highlighted ? 'text-[#c9a962]' : 'text-[#0a0a0a]'} />
            </div>
            <span className={`body-regular ${pkg.highlighted ? 'text-white' : ''}`}>{feature}</span>
          </li>
        ))}
      </ul>
      
      {/* CTA */}
      <Button 
        asChild
        className={`w-full rounded-none py-4 h-auto text-sm font-medium transition-all duration-300 ${
          pkg.highlighted 
            ? 'bg-white text-[#0a0a0a] hover:bg-white/90 border-none' 
            : 'bg-transparent text-[#c9a962] border border-[#c9a962] hover:bg-[#c9a962] hover:text-[#0a0a0a]'
        }`}
      >
        <a href="#contact">{pkg.cta}</a>
      </Button>
    </div>
  );
};

const WebDesignPackages = () => {
  return (
    <section id="web-design" className="section-padding bg-[#0a0a0a]">
      <div className="container">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-sm font-medium text-[#c9a962] tracking-wider uppercase mb-4">
            Website Design
          </span>
          
          <h2 className="heading-1 text-white mb-6">
            Website Design Packages
          </h2>
          
          <p className="body-large text-[#b3b3b3]">
            Professional, modern websites that help your business stand out online. 
            From simple one-page sites to full e-commerce stores — I'll create something 
            that looks amazing and actually converts visitors into customers.
          </p>
        </div>
        
        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {webDesignPackages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} />
          ))}
        </div>
        
        {/* Add-Ons Section */}
        <div className="bg-[#111111] border border-[#2a2a2a] p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <Plus size={24} className="text-[#c9a962]" />
            <h3 className="heading-2 text-white">Optional Add-Ons</h3>
          </div>
          
          <p className="body-regular text-[#b3b3b3] mb-8">
            Enhance your website with these additional services. Perfect for businesses that want extra support.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {webDesignAddOns.map((addon, index) => (
              <div 
                key={index}
                className="p-4 bg-[#0a0a0a] border border-[#2a2a2a] hover:border-[#c9a962]/30 transition-colors"
              >
                <h4 className="heading-3 text-white mb-1">{addon.name}</h4>
                <p className="body-small text-[#808080]">{addon.description}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Custom Quote CTA */}
        <div className="text-center mt-12">
          <p className="body-large text-[#b3b3b3] mb-2">
            Need something different?
          </p>
          <p className="body-regular text-[#808080] mb-6">
            Every business is unique. Get in touch for a custom quote tailored to your specific needs.
          </p>
          <Button 
            asChild
            className="bg-transparent text-[#c9a962] border border-[#c9a962] hover:bg-[#c9a962] hover:text-[#0a0a0a] rounded-none px-8 py-4 h-auto text-sm font-medium transition-all duration-300"
          >
            <a href="#contact" className="inline-flex items-center gap-2">
              Request a Custom Quote
              <ArrowRight size={16} />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default WebDesignPackages;
